import { Box, Tab, Tabs, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { IFilterItem } from '../../../types/shell';

interface IProps {
  tabs: IFilterItem[];
}

const ToggleSwitch: React.FC<IProps> = ({ tabs }) => {
  const theme = useTheme();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [maxTabWidth, setMaxTabWidth] = useState<number | undefined>(undefined);

  const activeTab = useMemo(() => tabs.find((tab) => tab.isActive), [tabs]);
  const value = useMemo(() => activeTab?.key || tabs[0]?.key, [activeTab, tabs]);

  // On first render finds longest tab width and set it to each tab
  useLayoutEffect(() => {
    const tabElements = Object.values(tabRefs.current).filter((tab): tab is HTMLButtonElement => tab !== null);

    if (tabElements.length > 0) {
      const widths = tabElements.map((tab) => tab.offsetWidth);
      const max = Math.max(...widths);
      setMaxTabWidth(max + 10);
    }
  }, []);

  // On tab change updates toggle(motion.div) position
  useLayoutEffect(() => {
    const activeOption = tabRefs.current[value];
    const container = containerRef.current;

    if (activeOption && container) {
      const tabRect = activeOption.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      setIndicatorStyle({
        left: tabRect.left - containerRect.left,
        width: tabRect.width,
      });
    }
  }, [value, maxTabWidth]);

  const toggleOptions = useMemo(() => {
    return tabs.map((tab) => (
      <Tab
        key={tab.key}
        value={tab.key}
        label={tab.label}
        ref={(el) => (tabRefs.current[tab.key] = el as HTMLButtonElement | null)}
        disableRipple
        {...(tab.dataAction ? { 'data-action': tab.dataAction } : {})}
        sx={{
          fontSize: 13,
          p: 1,
          lineHeight: '14px',
          minHeight: 0,
          minWidth: maxTabWidth || 'auto',
          width: maxTabWidth || 'auto',
          height: 'auto',
          zIndex: 1,
          alignItems: 'center',
          textTransform: 'none',
          '&.Mui-selected': {
            color: theme.sass.ebony,
          },
        }}
      />
    ));
  }, [tabs, maxTabWidth, theme]);

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: string) => tabs.find((tab) => tab.key === newValue)?.onClick(),
    [tabs],
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        backgroundColor: theme.palette.grey[200],
        height: 'auto',
        px: 1,
        py: 0.625,
        borderRadius: 0.5,
        boxShadow: `${theme.sass.lightShadow} inset`,
      }}
    >
      <motion.div
        layout
        transition={{ type: 'spring', stiffness: 500, damping: 35 }}
        style={{
          position: 'absolute',
          top: 5,
          bottom: 5,
          left: indicatorStyle.left,
          width: indicatorStyle.width,
          backgroundColor: theme.palette.common.white,
          zIndex: 0,
          borderRadius: 4,
          boxShadow: theme.sass.lightShadow,
        }}
      />
      <Tabs
        value={value}
        onChange={handleChange}
        sx={{
          zIndex: 1,
          minHeight: 0,
          height: 'auto',
          '& .MuiTabs-indicator': { display: 'none' },
        }}
      >
        {toggleOptions}
      </Tabs>
    </Box>
  );
};

export default ToggleSwitch;
